import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useAddSightSeenMutation } from "../queries/tourAreaQueries";

const AddSightseen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  
  // Form state
  const [heading, setHeading] = useState("");
  const [closingTime, setClosingTime] = useState("");
  const [entryFee, setEntryFee] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);
  
  // RTK Query mutation
  const [addSightSeen, { isLoading }] = useAddSightSeenMutation();
  
  // Handle form submission
  const handleSubmit = async () => {
    // Validate form fields
    if (!heading || !closingTime || !content || !entryFee || !image) {
      toast({
        title: "Missing fields",
        description: "Please fill all the required fields",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      // Create FormData object
      const formData = new FormData();
      formData.append("heading", heading);
      formData.append("closingTime", closingTime);
      formData.append("content", content);
      formData.append("entryFee", entryFee);
      formData.append("image", image);
      formData.append("tourAreaId", id); // Add the tour area ID from URL params
      
      // Submit the form
      const response = await addSightSeen(formData).unwrap();
      
      // Show success message
      toast({
        title: "Success!",
        description: response.message || "Sightseen added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Navigate back to sightseen listing
      navigate(`/tour-area/${id}/sightseen`);
    } catch (error) {
      toast({
        title: "Error",
        description: error?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // Custom styles for ReactQuill editor
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image', 'video']
    ]
  };

  return (
    <Box p={6} boxShadow="md" borderRadius="lg" bg="white" my="60px">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg" fontSize="24px">Add Sightseen</Heading>
        {/* <Button colorScheme="red" onClick={() => navigate(`/tour-area/${id}/sightseen`)}>Cancel</Button> */}
      </Flex>
      <Flex direction="column" gap={5}>
        <Box>
          <Box mb={2} fontSize="15px" fontWeight="500">Heading</Box>
          <Input 
            placeholder="Enter heading" 
            size="md" 
            height="42px" 
            fontSize="15px" 
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
          />
        </Box>
        <Flex gap={5}>
          <Box flex="1">
            <Box mb={2} fontSize="15px" fontWeight="500">Closing Time</Box>
            <Input 
              placeholder="Enter closing time (e.g. 08:00 AM-06:00 PM)" 
              size="md" 
              height="42px" 
              fontSize="15px" 
              value={closingTime}
              onChange={(e) => setClosingTime(e.target.value)}
            />
          </Box>
          <Box flex="1">
            <Box mb={2} fontSize="15px" fontWeight="500">Entry Fee</Box>
            <Input 
              placeholder="Enter entry fee (e.g. 100/-)" 
              size="md" 
              height="42px" 
              fontSize="15px" 
              value={entryFee}
              onChange={(e) => setEntryFee(e.target.value)}
            />
          </Box>
        </Flex>
        <Box>
          <Box mb={2} fontSize="15px" fontWeight="500">Content</Box>
          <ReactQuill
            value={content}
            onChange={setContent}
            theme="snow"
            placeholder="Enter description"
            modules={quillModules}
            style={{ height: '250px', marginBottom: '35px', fontSize: '15px' }}
          />
        </Box>
        <Box>
          <Box mb={2} fontSize="15px" fontWeight="500">Image</Box>
          <Input 
            type="file" 
            height="42px" 
            padding="8px" 
            onChange={(e) => setImage(e.target.files[0])}
          />
        </Box>
        <Button 
          colorScheme="blue" 
          mt={5} 
          size="md" 
          height="40px" 
          fontSize="15px" 
          isLoading={isLoading}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Flex>
    </Box>
  );
};

export default AddSightseen;
